import React, { useState, useMemo } from 'react';
import { locations, regions, serviceTypes } from './data/locations';
import './App.css';

function App() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedServiceType, setSelectedServiceType] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState(null);

  const filteredLocations = useMemo(() => {
    return locations.filter(location => {
      const matchesSearch = location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          location.address.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRegion = !selectedRegion || location.region === selectedRegion;
      
      // 修正服務類型篩選邏輯
      let matchesService = false;
      if (selectedServiceType === 'all') {
        matchesService = true;
      } else if (selectedServiceType === '貼膜') {
        // 選擇貼膜時，顯示有貼膜服務的據點（包括只有貼膜的和雙重服務的）
        matchesService = location.serviceType === '貼膜' || location.serviceType === '雙重服務';
      } else if (selectedServiceType === '客製') {
        // 選擇客製時，顯示有客製服務的據點（包括只有客製的和雙重服務的）
        matchesService = location.serviceType === '客製' || location.serviceType === '雙重服務';
      } else if (selectedServiceType === '雙重服務') {
        // 選擇雙重服務時，只顯示同時有兩種服務的據點
        matchesService = location.serviceType === '雙重服務';
      }
      
      return matchesSearch && matchesRegion && matchesService;
    });
  }, [searchTerm, selectedRegion, selectedServiceType]);

  const handleLocationClick = (location) => {
    setSelectedLocation(location);
  };

  const closeModal = () => {
    setSelectedLocation(null);
  };

  const getServiceIcon = (serviceType) => {
    if (serviceType === '貼膜') return '📱';
    if (serviceType === '客製') return '🎨';
    return '⚡';
  };

  const openGoogleMaps = (address) => {
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/${encodedAddress}`, '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="header">
        <div className="container">
          <h1>分分鐘據點地圖</h1>
          <p>🤖 90秒AI貼膜 + 🎨 3分鐘客製手機殼</p>
        </div>
      </header>

      {/* Search and Filter Section */}
      <div className="search-section">
        <div className="search-container">
          {/* Search Bar */}
          <input
            type="text"
            placeholder="搜尋據點名稱或地址..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />

          {/* Filters */}
          <div className="filters">
            {/* Region Filter */}
            <div className="filter-group">
              <label>選擇地區</label>
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                className="filter-select"
              >
                <option value="">全部地區</option>
                {regions.map(region => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </select>
            </div>

            {/* Service Type Filter */}
            <div className="filter-group">
              <label>服務類型</label>
              <div className="service-buttons">
                {serviceTypes.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedServiceType(type.id)}
                    className={`service-button ${type.id} ${
                      selectedServiceType === type.id ? 'active' : 'inactive'
                    }`}
                  >
                    {type.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="results-count">
        找到 <span className="count">{filteredLocations.length}</span> 個據點
      </div>

      {/* Locations Grid */}
      <div className="container">
        <div className="locations-grid">
          {filteredLocations.map(location => (
            <div
              key={location.id}
              className="location-card"
              onClick={() => handleLocationClick(location)}
            >
              <div className="location-card-content">
                <div className="location-header">
                  <h3 className="location-name">{location.name}</h3>
                  <span className={`service-badge ${location.serviceType}`}>
                    {getServiceIcon(location.serviceType)}
                  </span>
                </div>
                
                <div className="location-info">📍 {location.address}</div>
                <div className="location-info">🏙️ {location.city}</div>
                <div className="location-info">⏰ {location.hours}</div>
                
                <div className="features">
                  {location.features.map((feature, index) => (
                    <span key={index} className="feature-tag">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {selectedLocation && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-body">
              <div className="modal-header">
                <h2 className="modal-title">{selectedLocation.name}</h2>
                <button onClick={closeModal} className="modal-close">
                  ×
                </button>
              </div>
              
              <div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <span className={`service-badge ${selectedLocation.serviceType}`}>
                    {selectedLocation.service}
                  </span>
                </div>
                
                <div className="modal-info"><strong>📍 地址：</strong>{selectedLocation.address}</div>
                <div className="modal-info"><strong>🏙️ 城市：</strong>{selectedLocation.city}</div>
                 <div className="modal-info"><strong>⏰ 營業時間：</strong>{selectedLocation.hours}</div>
                
                <div className="features">
                  <strong style={{ color: '#374151' }}>✨ 特色：</strong>
                  <div className="modal-features">
                    {selectedLocation.features.map((feature, index) => (
                      <span key={index} className="modal-feature-tag">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
                
                <button
                  onClick={() => openGoogleMaps(selectedLocation.address)}
                  className="nav-button"
                >
                  🗺️ 開啟Google地圖導航
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3>分分鐘科技</h3>
              <p className="subtitle">便捷、高效、務實</p>
              <a href="https://minute-store.com" className="home-link">
                🏠 回到官網首頁
              </a>
            </div>
            
            <div className="footer-contact">
              <h4>聯絡我們</h4>
              <div className="contact-item">
                <span>📧 Email:</span>
                <a href="mailto:tolove058@gmail.com">tolove058@gmail.com</a>
              </div>
              <div className="contact-item">
                <span>📍 公司地址:</span>
                <span>台中市北區中清路一段603號</span>
              </div>
              <div className="contact-item">
                <span>📱 LINE:</span>
                <a href="https://line.me/ti/p/@3min">@3min</a>
              </div>
              <div className="contact-item">
                <span>📱 IG:</span>
                <a href="https://www.instagram.com/9090sec/">@9090sec</a>
              </div>
            </div>
            
            <div className="footer-stats">
              <div className="stat-item">
                <span>📍 全台 58個據點</span>
              </div>
              <div className="stat-item">
                <span>⏰ 24小時服務</span>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p className="copyright">© 2024 分分鐘科技. 版權所有</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
